import React, { FunctionComponent, MouseEvent, useState } from 'react';
import { Field } from 'formik';
import { Colors, Typography, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { useLocation } from 'react-router';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';
import useBoolean from 'hooks/useBoolean';

// constants
import Rules from 'restrictions/roles/constants/rules';

// types
import { ItemType } from 'ducks/merchantDucks/items/actions';

// components
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import TitleWithIcon from 'components/TitleWithIcon';
import CursorTooltip from 'pages/MerchantPages/Assets/components/Navbar/CursorTooltip';
import CommonIcon from 'components/CommonIcon';
import VoucherTypeIcon from 'pages/MerchantPages/Assets/Vouchers/components/VoucherTypeIcon';

const FieldStyled = styled(Field)`
  input {
    border-top: none;
    border-left: none;
    border-right: none;
    font-weight: ${({ theme }) => theme.font.weights.thin};
    font-size: ${({ theme }) => theme.font.sizes.large};
    padding: 0;
    background-color: transparent;
    border-radius: unset;
    width: auto;
  }
  padding-left: 1rem;
`;

const ItemTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 30%;
`;

const TitleWithIconWrapper = styled.div`
  padding-left: 1rem;
  > svg {
    width: 1rem;
    height: 1rem;
    color: ${Colors.yellow};
  }
  :hover {
    & > svg {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

interface CommonIconWrapperProps {
  showCursorTooltip: boolean;
}

const IconWrapper = styled.div<CommonIconWrapperProps>`
  span {
    width: 1rem;
    height: 1rem;
    margin-left: 0.5rem;
    > svg {
      vertical-align: middle;
      color: ${({ showCursorTooltip }) => (showCursorTooltip ? Colors.fontDarkGray : Colors.darkGray)};
    }
  }
`;

const Title = styled(Typography)`
  font-size: ${({ theme }) => theme.font.sizes.large};
  border-bottom: 1px solid ${Colors.fontLightGray};
`;

interface Props {
  title: string;
  itemType: string;
  showTitleWithIcon: boolean;
  hideTitleWithIcon: () => void;
  saveTitle: () => void;
}

const ItemTitle: FunctionComponent<Props> = ({ title, itemType, showTitleWithIcon, saveTitle, hideTitleWithIcon }) => {
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  const showCursorTooltip = useBoolean(false);
  const [customTooltipStyle, setCustomTooltipStyle] = useState({});

  const handleShowCursorTooltip = (show: boolean) => () => {
    if (isReadOnly) {
      return;
    }

    showCursorTooltip.setValue(show);
  };

  const handleCursorPosition = (e: MouseEvent<HTMLDivElement>) => {
    setCustomTooltipStyle({
      left: e.pageX,
      top: e.pageY - 20,
    });
  };

  const handleTitleClick = () => {
    if (isReadOnly) {
      return;
    }

    hideTitleWithIcon();
    showCursorTooltip.setFalse();
  };

  const isAsset = itemType.includes('asset');
  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');
  const itemName = isAsset ? 'asset' : 'package';
  const tagName = isEditTemplate ? 'template' : itemName;
  const isVoucher = itemType === ItemType.Voucher;

  return (
    <ItemTitleContainer>
      {isVoucher && <VoucherTypeIcon />}
      {showTitleWithIcon ? (
        <AnalyticsComponent>
          {({ pages, tracker, merchantId, ip }) => (
            <>
              <TitleWithIconWrapper
                onClick={() => {
                  tracker.track({
                    event: 'click',
                    type: 'icon',
                    tag: `label_${isVoucher ? 'voucher' : tagName}_name_editable`,
                    pages,
                    merchantId,
                    ip,
                  });
                  return handleTitleClick();
                }}
                onMouseOver={handleShowCursorTooltip(true)}
                onMouseOut={handleShowCursorTooltip(false)}
                onMouseMove={handleCursorPosition}
              >
                <TitleWithIcon
                  tag={`label_${isVoucher ? 'voucher' : tagName}_name_editable`}
                  title={title}
                  variant="label"
                  icon="edit"
                  iconPosition="right"
                >
                  <Title variant="p">{title}</Title>
                  <IconWrapper showCursorTooltip={showCursorTooltip.value}>
                    <CommonIcon name="edit" />
                  </IconWrapper>
                </TitleWithIcon>
              </TitleWithIconWrapper>
              {showCursorTooltip.value && <CursorTooltip style={customTooltipStyle} content="Click to Edit Name" />}
            </>
          )}
        </AnalyticsComponent>
      ) : (
        <>
          <FieldStyled
            name="title"
            type="text"
            component={Input}
            placeholder={`Name your ${isAsset ? 'asset' : itemType}`}
            disabled={isReadOnly}
            onBlur={saveTitle}
            autoFocus
          />
          <FieldError name="title" />
        </>
      )}
    </ItemTitleContainer>
  );
};

export default ItemTitle;
